import { EventEmitter, Injectable, Optional } from '@angular/core';
import {
  Auth,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  verifyPasswordResetCode,
  confirmPasswordReset,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  OAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  User as IFBUser,
  AuthCredential,
  UserCredential,
  checkActionCode,
  applyActionCode,
  signInWithEmailAndPassword,
  SAMLAuthProvider,
  signInWithEmailLink,
  AuthErrorCodes,
  signInWithCustomToken,
  signInWithRedirect,
  signInWithCredential,
  signInAnonymously,
  updateProfile,
  updateEmail,
} from '@angular/fire/auth';
import { captureException } from '@sentry/angular';
import { BehaviorSubject, filter, from, Observable, of, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomErrorCodes } from 'src/custom_error_codes.constants';
import { isEmpty } from 'lodash-es';
import { environment } from 'src/environments/environment';
import { LogoutDialogComponent } from '../dialogs/logout/logout.component';
import {
  extractReferralParams,
  extractTemplateIdParam,
  extractUTMParams,
  LANDING_URL,
} from '../common/utils/url';
import { User as IUser } from '../models/user';
import { ERRORS } from '../common/utils/notification-constants';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconMessageToasterElement } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { EmbeddedGoogleLoginComponent } from '../dialogs/embedded-google-login/embedded-google-login.component';
import { AccountsConstantsTransformer, PROVIDERS_ALL } from '../account/account.constants';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { extractActionsQueryParams } from '../common/utils/actions-query-params-util';
import { AuthTracing } from '../utilities/auth-tracing.utils';
import { UserService } from './user.service';
import {
  NotificationDataBuilder,
  NotificationToasterService,
  NotificationType,
} from './notification-toaster.service';

// Google Identity Services, loaded by index.html (accounts.google.com/gsi/client).
declare const google: any;

export enum LOGIN_TYPE {
  SignIn = 'SignIn',
  SignUp = 'SignUp',
  APISignIn = 'api',
  TutorCruncherSignIn = 'tutorcrunchersignin',
  Clever = 'clever',
  Classlink = 'classlink',
  LTI = 'lti',
  Teams = 'teams',
  Zoom = 'zoom',
}

export interface SignInParameters {
  login_hint?: string;
  type: LOGIN_TYPE;
  authString?: string;
  doRedirect?: boolean;
  token?: string;
  continueUrl?: string;
  redirectUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  fbUser: BehaviorSubject<IFBUser | null | undefined> = new BehaviorSubject<
    IFBUser | null | undefined
  >(undefined);
  loading: Promise<void>;
  providerMap = {
    'google.com': this.doGoogleLogin,
    'facebook.com': this.doFacebookLogin,
    'microsoft.com': this.doMicrosoftLogin,
    'apple.com': this.doAppleLogin,
    oidc: this.doOIDCLogin,
    saml: this.doSAMLLogin,
  };
  continueUrl!: string;
  /** Raw continueUrl captured at construction time before Angular can navigate away. */
  private readonly rawContinueUrl: string | null = new URLSearchParams(window.location.search).get(
    'continueUrl',
  );
  justSignedOut = false;
  emailVerified = false;
  logOutEmitter = new EventEmitter();
  PROVIDERS_TO_NAMES;

  constructor(
    @Optional() private auth: Auth,
    private userService: UserService,
    private dialog: MatDialog,
    private router: Router,
    public activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private notificationsService: NotificationToasterService,
  ) {
    let hash = window.location.hash?.substring(1);
    if (hash === LOGIN_TYPE.SignUp || hash === LOGIN_TYPE.SignIn) {
      this.userService.setLoginFlowLoading(true);
    }

    const accountsConstants = new AccountsConstantsTransformer(this.translate);
    this.PROVIDERS_TO_NAMES = accountsConstants.providersToNames;

    // hide after 10 seconds if still on login and no change took place
    modifiedSetTimeout(() => {
      if (hash === LOGIN_TYPE.SignUp || hash === LOGIN_TYPE.SignIn) {
        this.userService.setLoginFlowLoading(false);
      }
    }, 10000);
    this.loading = new Promise((resolve, reject) => {
      onAuthStateChanged(this.auth, async (user) => {
        if (user) {
          try {
            // If the user is already logged in and this is an MCP OAuth callback,
            // redirect immediately with the refresh token instead of going through
            // the normal login flow.
            if (this.performRedirect()) {
              resolve();
              return;
            }
            const loginType = hash as LOGIN_TYPE;
            if (loginType !== LOGIN_TYPE.SignIn && loginType !== LOGIN_TYPE.SignUp) {
              await AuthTracing.logWithTokenContext('AuthService.existing_session_resumed', user);
            }
            await this.handleFirebaseUser(user, loginType);
            // If the hash isn't set to empty, when the auth changes (token expiry etc)
            // it will try to handle the user as a full login again instead of simply
            // refreshing the token
            hash = '';
          } catch (error) {
            reject(error);
          }
        } else {
          // otherwise design breaks for a split second before being redirected to login
          AuthTracing.log('AuthService.onAuthStateChanged(user=null)');
          if (!this.justSignedOut) {
            this.fbUser.next(null);
          }
          this.justSignedOut = true;
          this.logOutEmitter.emit();
        }
        resolve();
      });
    });
    onIdTokenChanged(this.auth, async (user) => {
      if (user) {
        await AuthTracing.logWithTokenContext('AuthService.onIdTokenChanged', user);
      }
    });
  }

  public getRedirectUrl(): string {
    // If the continueUrl provided is provided in the current URL, use it. Otherwise, redirect to appropriate
    // destination based on user type
    let continueUrl = this.activatedRoute.snapshot.queryParams['continueUrl'];
    if (!continueUrl) {
      const secondaryContinueUrl = this.router.parseUrl(location.pathname + location.search)
        .queryParams?.continueUrl;
      continueUrl = secondaryContinueUrl ? secondaryContinueUrl : LANDING_URL;
    }

    // append any remaining params from original url to continueUrl
    const continueUrlObj = new URL(continueUrl, window.location.origin);

    // Convert URLSearchParams to plain object
    const continueUrlParams: Record<string, string> = {};
    continueUrlObj.searchParams.forEach((value, key) => {
      continueUrlParams[key] = value;
    });

    const params = Object.assign(
      extractUTMParams(this.activatedRoute.snapshot.queryParamMap),
      extractReferralParams(this.activatedRoute.snapshot.queryParamMap),
      extractTemplateIdParam(this.activatedRoute.snapshot.queryParamMap),
      extractActionsQueryParams(),
      continueUrlParams,
    );

    const pathname = continueUrlObj.pathname;
    if (!isEmpty(params)) {
      const paramsObj = new URLSearchParams(params);
      return `${pathname}?${paramsObj.toString()}`;
    }
    return pathname;
  }

  public getAbsoluteRedirectUrl(): URL {
    return new URL(this.getRedirectUrl(), window.location.origin);
  }

  async handleFirebaseUser(user: IFBUser, loginType: LOGIN_TYPE) {
    this.fbUser.next(user);

    return new Promise<IUser | undefined>((resolve, reject) => {
      if (loginType === LOGIN_TYPE.SignIn || loginType === LOGIN_TYPE.SignUp) {
        // If the user just signed in, we need to handle sign up and redirect to the requested URL.
        this.userService.createUser(user.providerData[0]?.providerId !== 'password').subscribe({
          next: (res) => {
            this.continueUrl = this.getRedirectUrl();
            // Skip MCP OAuth redirect for new signups — only existing users
            // (SignIn) should complete the MCP flow. New accounts are not
            // institution admins and the public API would reject their requests.
            //
            // Also detect first-time social auth signups (Google, Apple, etc.)
            // via Firebase metadata: creationTime === lastSignInTime means
            // the user was just created (first sign-in ever).
            const isFirstTimeSignup =
              loginType === LOGIN_TYPE.SignUp ||
              user.metadata?.creationTime === user.metadata?.lastSignInTime;
            if (isFirstTimeSignup) {
              this.router.navigateByUrl(this.continueUrl);
            } else {
              this.performRedirect(this.continueUrl);
            }
            resolve(res);
          },
          error: (err) => {
            reject(err);
          },
        });
      } else {
        resolve(undefined);
      }
    });
  }

  get firebaseUser(): Observable<IFBUser | null | undefined> {
    return this.fbUser.asObservable();
  }

  async refreshUser() {
    return this.auth?.currentUser?.reload();
  }

  async userName(): Promise<string> {
    const displayName = this.auth.currentUser?.displayName;
    if (displayName) {
      return displayName;
    }
    return 'None';
  }

  photo(): string {
    const photoURL = this.auth.currentUser?.photoURL;
    if (photoURL) {
      return photoURL;
    }
    return '';
  }

  getToken(): Observable<string> {
    const idToken = this.auth.currentUser?.getIdToken();
    if (idToken) {
      return from(idToken);
      // Return from(this.fireuser.getIdToken(true));
    } else {
      return of('');
    }
  }

  getRefreshToken(): string {
    return this.auth.currentUser?.refreshToken ?? '';
  }

  async currentToken(): Promise<string | undefined> {
    const idToken = await this.auth.currentUser?.getIdToken();
    return idToken;
  }

  verifyPasswordResetCode(code: string): Promise<string> {
    return verifyPasswordResetCode(this.auth, code);
  }

  passwordReset(code: string, newPassword: string) {
    return confirmPasswordReset(this.auth, code, newPassword);
  }

  public async anonymousLogin(displayName: string) {
    const { user: fbUser } = await signInAnonymously(this.auth);
    if (!fbUser) {
      throw new Error('Unable to create user');
    }
    await updateProfile(fbUser, { displayName });
    await updateEmail(fbUser, `${fbUser.uid}@anonymous.pencilspaces.com`);
    await this.handleFirebaseUser(fbUser, LOGIN_TYPE.SignUp);
  }

  async doEmailSignup(
    {
      email,
      password,
    }: {
      email: string;
      password: string;
    },
    verifyToken = '',
  ): Promise<void> {
    const { user: fbUser } = await createUserWithEmailAndPassword(this.auth, email, password);
    if (!fbUser) {
      throw new Error('Unable to create user');
    }

    if (verifyToken) {
      this.userService
        .verifyToken(verifyToken, email)
        .pipe(take(1))
        .subscribe({
          next: async () => {
            await this.doLogin({ email, password }, true);
            this.userService.sendWelcomeEmail().pipe(take(1)).subscribe();
          },
          error: () => {
            // In case of invalid token, proceed normally
            this.handleFirebaseUser(fbUser, LOGIN_TYPE.SignUp);
          },
        });
    } else {
      await this.handleFirebaseUser(fbUser, LOGIN_TYPE.SignUp);
    }
  }

  async doGoogleLogin(params: SignInParameters) {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');

    let customParams = {
      prompt: 'select_account',
    };
    const hint = params.login_hint;
    if (hint) {
      customParams = {
        ...customParams,
        ...{ hint },
      };
    }

    provider.setCustomParameters(customParams);

    // detect embedded webview
    // http://jsfiddle.net/ThinkingStiff/6qrbn/
    const iosStandalone = window.navigator['standalone'];
    const userAgent = window.navigator.userAgent.toLowerCase();
    // The line below evaluates to true for all ios browsers, not just safari
    const iosBrowser = /safari/.test(userAgent);
    const ios = /iphone|ipod|ipad/.test(userAgent);

    if ((ios && !iosStandalone && !iosBrowser) || (!ios && userAgent.includes('wv'))) {
      this.dialog.open(EmbeddedGoogleLoginComponent, {
        width: '330px',
      });
    } else {
      try {
        const result = environment.googleSignInClientId
          ? await signInWithCredential(
              this.auth,
              GoogleAuthProvider.credential(null, await this.requestGoogleAccessToken(hint)),
            )
          : await signInWithPopup(this.auth, provider);
        if (result.user) {
          this.handleFirebaseUser(result.user, params.type);
        }
      } catch (e) {
        e.customData = { provider: PROVIDERS_ALL.GOOGLE };
        this.handlePopupError(e);
        throw new Error(e);
      }
    }
  }

  /**
   * Google sign-in through this build's own OAuth client, so the consent screen shows
   * this build's brand. Firebase's popup always uses the Firebase project's client;
   * this one lives in another Cloud project and is safelisted on the Firebase Google
   * provider, so the token signs in the same Firebase user (same Google account id).
   * Errors are shaped like Firebase's so handlePopupError treats both paths alike.
   */
  private requestGoogleAccessToken(loginHint?: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined' || !google.accounts?.oauth2) {
        reject({ code: AuthErrorCodes.INTERNAL_ERROR, message: 'Google sign-in is still loading' });
        return;
      }
      const client = google.accounts.oauth2.initTokenClient({
        client_id: environment.googleSignInClientId,
        scope: 'openid email profile',
        prompt: 'select_account',
        ...(loginHint ? { login_hint: loginHint } : {}),
        callback: (response: { access_token?: string; error?: string; error_description?: string }) => {
          if (response.access_token) {
            resolve(response.access_token);
          } else {
            reject({
              code:
                response.error === 'access_denied'
                  ? AuthErrorCodes.USER_CANCELLED
                  : AuthErrorCodes.INTERNAL_ERROR,
              message: response.error_description || response.error,
            });
          }
        },
        error_callback: (err: { type?: string; message?: string }) => {
          reject({
            code:
              err?.type === 'popup_failed_to_open'
                ? AuthErrorCodes.POPUP_BLOCKED
                : err?.type === 'popup_closed'
                  ? AuthErrorCodes.POPUP_CLOSED_BY_USER
                  : AuthErrorCodes.INTERNAL_ERROR,
            message: err?.message,
          });
        },
      });
      client.requestAccessToken();
    });
  }

  handleClassLinkLogin() {
    window.location.href = `https://launchpad.classlink.com/oauth2/v2/auth?scope=profile&response_type=code&redirect_uri=${encodeURIComponent(
      environment.classlink.redirectURI,
    )}&client_id=${environment.classlink.clientId}&state=${this.getOauthState()}`;
  }

  handleCleverLogin() {
    window.location.href = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${encodeURIComponent(
      environment.clever.redirectURI,
    )}&client_id=${environment.clever.clientId}&state=${this.getOauthState()}`;
  }

  private getOauthState(): string {
    const state = JSON.stringify({ continueUrl: this.getRedirectUrl() });
    return btoa(state);
  }

  async doOIDCLogin(params: SignInParameters) {
    if (!params.authString) {
      return;
    }
    const provider = new OAuthProvider(`oidc.${params.authString}`);
    try {
      if (params.token) {
        const rawCredential = {
          idToken: params.token,
        };
        const credential = provider.credential(rawCredential);
        const result = await signInWithCredential(this.auth, credential);
        this.handleFirebaseUser(result.user, params.type);
      } else if (params.doRedirect) {
        await signInWithRedirect(this.auth, provider);
      } else {
        const result = await signInWithPopup(this.auth, provider);
        this.handleFirebaseUser(result.user, params.type);
      }
    } catch (e) {
      e.customData = { provider: `oidc.${params.authString}` };
      // Token error is handled separately
      if (!params.token) {
        console.log(e);
        this.handlePopupError(e);
      }
      throw e;
    }
  }

  async doMicrosoftLogin(params: SignInParameters) {
    const provider = new OAuthProvider('microsoft.com');
    provider.addScope('User.Read');

    try {
      const result = await signInWithPopup(this.auth, provider);
      if (result.user) {
        this.handleFirebaseUser(result.user, params.type);
      }
    } catch (e) {
      e.customData = { provider: PROVIDERS_ALL.MICROSOFT };
      this.handlePopupError(e);
      throw new Error(e);
    }
  }

  async doAppleLogin(params: SignInParameters) {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    try {
      const result = await signInWithPopup(this.auth, provider);
      if (result.user) {
        this.handleFirebaseUser(result.user, params.type);
      }
    } catch (e) {
      e.customData = { provider: PROVIDERS_ALL.APPLE };
      this.handlePopupError(e);
      throw new Error(e);
    }
  }

  async doFacebookLogin(params: SignInParameters) {
    const provider = new FacebookAuthProvider();
    provider.addScope('public_profile');
    provider.addScope('email');
    let customParams = {
      prompt: 'select_account',
    };
    const hint = params.login_hint;
    if (hint) {
      customParams = {
        ...customParams,
        ...{ hint },
      };
    }
    provider.setCustomParameters(customParams);
    try {
      const result = await signInWithPopup(this.auth, provider);
      if (result.user) {
        this.handleFirebaseUser(result.user, params.type);
      }
    } catch (e) {
      e.customData = {
        ...e.customData,
        ...{ provider: PROVIDERS_ALL.FACEBOOK },
      };
      this.handlePopupError(e);
      throw e;
    }
  }

  async doSAMLLogin(params: SignInParameters) {
    if (params.authString) {
      const provider = new SAMLAuthProvider(`saml.${params.authString}`);
      this.userService.setLoginFlowLoading(false);
      await signInWithPopup(this.auth, provider)
        .then((result) => {
          if (result.user) {
            this.handleFirebaseUser(result.user, params.type);
          }
        })
        .catch((e) => {
          e.customData = { provider: `saml.${params.authString}` };
          this.handlePopupError(e);
          throw new Error(e);
        });
    }
  }

  /** Sign in with firebase, create Pencil account if !exists */
  async doLogin(value, verify = false): Promise<void> {
    this.userService.setLoginFlowLoading(true);
    await signInWithEmailAndPassword(this.auth, value.email, value.password);
    this.userService.getUser().subscribe({
      // if !pencil_user
      //    try createUser() -> new space
      //    catch -> redirect to login
      next: (userInfo) => {
        this.userService.setLoginFlowLoading(false);
        if (verify || !userInfo.status_code) {
          if (verify) {
            const updatedUser = this.getCurrentUser();
            this.fbUser.next(updatedUser);
          }

          const redirectUrl = this.getRedirectUrl();
          this.performRedirect(redirectUrl);
        } else if (userInfo.status_code === CustomErrorCodes.ACCOUNT_NOT_SIGNED_UP) {
          this.userService.createUser(false).subscribe({
            next: (user) => {
              const redirectUrl = this.getRedirectUrl();
              // New account — skip MCP OAuth redirect (not an institution admin)
              this.router.navigateByUrl(redirectUrl);
            },
            error: (e) => {
              captureException(new Error('Error creating user'), {
                extra: {
                  error: e,
                },
              });
              this.router.navigate(['account', 'login']);
            },
          });
        }
      },
      error: () => {
        this.userService.setLoginFlowLoading(false);
      },
    });
  }

  async verifyFirebaseAccount(code: string) {
    return applyActionCode(this.auth, code);
  }

  checkFirebaseToken(code: string) {
    return checkActionCode(this.auth, code);
  }

  getProviders(email: string): Promise<string[]> {
    return fetchSignInMethodsForEmail(this.auth, email);
  }

  /**
   * It links existing account with new provider
   * @param newCredential new Credential from a trial to sign in using the new Provider.
   * @param oldProvider the name of the old provider to log in through it
   * @param(Auth, email)to set a Email hint in the pop-up window.
   * @returns Promise of User credential.
   */
  async link(newCredential: AuthCredential, oldProvider: string, email: string) {
    this.continueUrl = this.activatedRoute.snapshot.queryParams['continueUrl'] || 'content';

    if (oldProvider.includes('saml')) {
      oldProvider = 'saml';
    }
    if (oldProvider.includes('oidc')) {
      oldProvider = 'oidc';
    }

    // In case of unsupported provider
    if (!this.providerMap[oldProvider]) {
      throw new Error(`Provider ${oldProvider} is not supported`);
    }
    // Login using the old provider.
    const cred = await this.providerMap[oldProvider](email);
    const currentUser = cred.user;

    if (!currentUser) {
      throw new Error('Login Fails.');
    }
    await this.linkWithCredential(currentUser, newCredential);
    location.href = this.continueUrl;
  }

  linkWithCredential(user: IFBUser, credential: AuthCredential): Promise<UserCredential> {
    return linkWithCredential(user, credential);
  }

  showLogoutConfirmationDialog(afterLogOutPath?: string): void {
    const dialogRef = this.dialog.open(LogoutDialogComponent, {
      width: '340px',
      panelClass: 'logout-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(filter((result) => result === 'confirm'))
      .subscribe(async () => {
        await this.signoutAndRedirect(afterLogOutPath);
      });
  }

  /**  is email used (includes all providers) */
  async isEmailUsed(email: string): Promise<boolean> {
    const roles = await fetchSignInMethodsForEmail(this.auth, email);
    return !!roles.length;
  }

  requestPasswordReset(email: string) {
    return new Promise((resolve, reject) => {
      this.userService
        .sendPasswordResetRequest({
          email: email,
        })
        .subscribe({
          next: () => {
            resolve(true);
          },
          error: (e) => {
            reject(e);
          },
        });
    });
  }

  signInWithEmailLink(email: string, emailLink: string) {
    return signInWithEmailLink(this.auth, email, emailLink);
  }

  signInWithCustomToken(customToken: string) {
    return signInWithCustomToken(this.auth, customToken);
  }

  getCurrentUser() {
    return this.auth.currentUser;
  }

  /**
   * The ID token sign-in provider of the user in `firebaseUser`: `custom` for Clever, ClassLink,
   * LTI and TutorCruncher, `password` / `google.com` / ... for direct signups. The token is parsed
   * locally; Firebase only goes to the network if the token is about to expire. Null if there
   * is no user or the token can't be read.
   */
  async getSignInProvider(): Promise<string | null> {
    try {
      const tokenResult = await this.fbUser.getValue()?.getIdTokenResult();
      return tokenResult?.signInProvider ?? null;
    } catch (error) {
      captureException(error);
      return null;
    }
  }

  async signout() {
    AuthTracing.log('AuthService.signout');
    this.userService.user.next(null);
    await this.auth?.signOut();
    await this.auth?.currentUser?.reload();
  }

  async signoutAndRedirect(redirectPath: string = '/account/login'): Promise<void> {
    AuthTracing.log('AuthService.signoutAndRedirect');
    this.justSignedOut = true;
    await signOut(this.auth);
    this.fbUser.next(null);
    location.href = redirectPath;
  }

  /**
   * Decodes a JWT token and returns its payload
   * @param token JWT token to decode
   * @returns Decoded token payload or null if invalid
   */
  decodeToken(token: string): any {
    try {
      // Split the token into parts
      const parts = token.split('.');

      if (parts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }

      // Get the payload (second part)
      const payload = parts[1];

      // Add padding if needed for proper base64 decoding
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);

      // Decode base64
      const decodedPayload = atob(paddedPayload);

      // Parse JSON
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Parses a packed token format (token~renewCode) used in inspect mode.
   * @param token The packed token string (may include ~ separator with renew code)
   * @returns Object with rawToken and optional renewCode
   */
  parsePackedToken(token: string): { rawToken: string; renewCode?: string } {
    if (!token) {
      return { rawToken: token };
    }
    const [rawToken, renewCode] = token.split('~');
    return { rawToken, renewCode };
  }

  showSuccessToast(message?: { title: string; description: string }): void {
    if (!message) {
      return;
    }

    const titleElement = new IconMessageToasterElement(
      undefined,
      this.translate.instant(message.title),
    );
    const messageElement = new IconMessageToasterElement(
      undefined,
      this.translate.instant(message.description),
    );

    const successNotification = new NotificationDataBuilder(ERRORS.UNSUPPORTED_SAML_PROVIDER)
      .style(ToasterPopupStyle.SUCCESS)
      .type(NotificationType.SUCCESS)
      .timeOut(5)
      .topElements([titleElement])
      .middleElements([messageElement])
      .dismissable(true)
      .build();
    this.notificationsService.showNotification(successNotification);
  }

  handleNetworkError(error): void {
    const titleElement = new IconMessageToasterElement(
      undefined,
      this.translate.instant('Network disconnected'),
    );
    const messageElement = new IconMessageToasterElement(
      undefined,
      this.translate.instant('Please check your internet connection and try again.'),
    );

    const networkDisconnectedError = new NotificationDataBuilder(ERRORS.NETWORK_DISCONNECTED)
      .style(ToasterPopupStyle.ERROR)
      .type(NotificationType.ERROR)
      .timeOut(10)
      .topElements([titleElement])
      .middleElements([messageElement])
      .dismissable(true)
      .build();
    this.notificationsService.showNotification(networkDisconnectedError);
  }

  /**
   * Attempts the MCP OAuth redirect if applicable, otherwise navigates to the
   * fallback URL via Angular Router. When called without a fallback (e.g. from
   * onAuthStateChanged for already-logged-in users), only the MCP redirect is
   * attempted — no navigation occurs for non-MCP cases.
   * @returns true if an MCP redirect was performed.
   */
  private performRedirect(fallbackUrl?: string): boolean {
    if (this.tryMcpOAuthRedirect(this.auth.currentUser?.refreshToken)) {
      return true;
    }
    if (fallbackUrl) {
      this.router.navigateByUrl(fallbackUrl);
    }
    return false;
  }

  /**
   * If the page was loaded with an MCP OAuth continueUrl and a refresh token
   * is available, redirects the browser to the MCP callback with the token.
   * @returns true if the redirect was performed.
   */
  private tryMcpOAuthRedirect(refreshToken: string | undefined): boolean {
    if (!this.rawContinueUrl || !refreshToken || !this.isExternalMcpCallback(this.rawContinueUrl)) {
      return false;
    }
    const url = new URL(this.rawContinueUrl);
    url.searchParams.set('code', refreshToken);
    window.location.href = url.toString();
    return true;
  }

  /**
   * Returns true if the URL is an external MCP OAuth callback that should
   * receive the Firebase refresh token. Validates against a strict allowlist
   * of origins configured per environment.
   */
  private isExternalMcpCallback(url: string): boolean {
    try {
      const parsed = new URL(url);
      const allowedBaseUrls: string[] = (environment as any).mcpOAuthCallbackOrigins || [];
      const fullUrl = parsed.origin + parsed.pathname;
      return allowedBaseUrls.some((base) => fullUrl === `${base}/oauth/callback`);
    } catch {
      return false;
    }
  }

  handlePopupError(err) {
    window.location.hash = '';
    this.fbUser.next(null);
    this.justSignedOut = true;

    switch (err.code) {
      case AuthErrorCodes.USER_CANCELLED:
      case AuthErrorCodes.POPUP_CLOSED_BY_USER:
        // User cancelled on purpose,
        // Display no error and go back to the page they started from
        break;
      case AuthErrorCodes.NEED_CONFIRMATION:
        // This error is handled in the account entry service
        break;
      case AuthErrorCodes.POPUP_BLOCKED:
        const popupBlockedTitle = new IconMessageToasterElement(
          undefined,
          this.translate.instant('Popup blocked'),
        );
        const popupBlockedMessage = new IconMessageToasterElement(
          undefined,
          this.translate.instant(
            'The login popup was blocked! Please enable popups and try again.',
          ),
        );

        const popupBlockedError = new NotificationDataBuilder(ERRORS.UNSUPPORTED_FB_ACCOUNT)
          .style(ToasterPopupStyle.ERROR)
          .type(NotificationType.ERROR)
          .timeOut(10)
          .topElements([popupBlockedTitle])
          .middleElements([popupBlockedMessage])
          .dismissable(true)
          .build();
        this.notificationsService.showNotification(popupBlockedError);
        break;
      case AuthErrorCodes.TIMEOUT:
        const titleElement = new IconMessageToasterElement(
          undefined,
          this.translate.instant('Login attempt timed out'),
        );
        const messageElement = new IconMessageToasterElement(
          undefined,
          this.translate.instant('The server took too long to respond. Please try again later.'),
        );

        const timeoutError = new NotificationDataBuilder(ERRORS.UNSUPPORTED_FB_ACCOUNT)
          .style(ToasterPopupStyle.ERROR)
          .type(NotificationType.ERROR)
          .timeOut(10)
          .topElements([titleElement])
          .middleElements([messageElement])
          .dismissable(true)
          .build();
        this.notificationsService.showNotification(timeoutError);

        break;
      default:
        let provider = err.customData.provider;
        if (Object.values(PROVIDERS_ALL).includes(err.customData.provider)) {
          provider = this.PROVIDERS_TO_NAMES[err.customData.provider];
        }
        if (provider.startsWith('saml.') || provider.startsWith('oidc.')) {
          provider = provider.slice(5);
        }

        const genericTitleElement = new IconMessageToasterElement(
          undefined,
          this.translate.instant('Error with Login'),
        );
        const genericMessageElement = new IconMessageToasterElement(
          undefined,
          this.translate.instant("We're having trouble logging you in with ") +
            provider +
            this.translate.instant('. Please try again later or use another login method.'),
        );

        const genericRedirectError = new NotificationDataBuilder(ERRORS.UNSUPPORTED_FB_ACCOUNT)
          .style(ToasterPopupStyle.ERROR)
          .type(NotificationType.ERROR)
          .timeOut(10)
          .topElements([genericTitleElement])
          .middleElements([genericMessageElement])
          .dismissable(true)
          .build();
        this.notificationsService.showNotification(genericRedirectError);
        break;
    }
    captureException(err);
    this.userService.setLoginFlowLoading(false);
  }
}
